import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Facebook,
  Business,
  CheckCircle,
  Analytics
} from '@mui/icons-material';

import metaService from '../../../../services/metaService';

const normalizeMetaKeyResponse = (payload) => {
  const normalizedPayload = payload?.data ?? payload;
  const tokenKey =
    normalizedPayload?.tokenKey ||
    normalizedPayload?.accessToken ||
    normalizedPayload?.access_token ||
    '';

  return {
    tokenKey: typeof tokenKey === 'string' ? tokenKey.trim() : '',
    appName: normalizedPayload?.appName || normalizedPayload?.applicationName || '',
    appId: normalizedPayload?.appId || normalizedPayload?.app_id || ''
  };
};

const MetaAuth = ({ onAuthSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [completeAuthData, setCompleteAuthData] = useState(null);

  const API_VERSION = process.env.REACT_APP_META_API_VERSION;
  const appId = process.env.REACT_APP_META_APP_ID;
  const urlRedirect = process.env.REACT_APP_META_CALLBACK;

  const leadAdsPermissions = process.env.REACT_APP_META_PERMISSIONS.split(',');

  const handleMetaLogin = async () => {
    if (!appId) {
      setError('App ID não configurado. Verifique a variável de ambiente REACT_APP_META_APP_ID.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess('');

    try {
      // Generate unique state for security
      const state = Math.random().toString(36).substring(7);

      // Meta API authorization URL for Lead Ads
      const authUrl = `https://www.facebook.com/${API_VERSION}/dialog/oauth?` +
        `client_id=${appId}&` +
        `redirect_uri=${encodeURIComponent(urlRedirect)}&` +
        `scope=${leadAdsPermissions.join(',')}&` +
        `response_type=code&` +
        `state=${state}&` +
        `auth_type=rerequest`;

      // Save session information
      sessionStorage.setItem('meta_oauth_state', state);
      sessionStorage.setItem('meta_app_id', appId);
      sessionStorage.setItem('meta_api_version', API_VERSION);

      // Open authorization window
      const authWindow = window.open(
        authUrl,
        'metaLeadAdsAuth',
        'width=600,height=700,scrollbars=yes,resizable=yes,location=yes'
      );

      if (!authWindow) {
        throw new Error('Não foi possível abrir a janela de autorização. Verifique se o popup foi bloqueado.');
      }

      setSuccess('Janela de autorização aberta. Complete a concessão de acesso...');

      // Listener for popup window messages
      const handleMessage = async (event) => {
        // Verify origin for security
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'META_AUTH_RESULT') {
          window.removeEventListener('message', handleMessage);
          clearInterval(checkWindowClosed);

          if (event.data.success && event.data.code) {
            setLoading(false);
            setSuccess('✅ Autorização concedida! Processando dados...');

            // Store authorization data
            const authResult = {
              platform: 'meta',
              authorizationCode: event.data.code,
              state: event.data.state,
              grantedAt: new Date().toISOString(),
              redirectUri: event.data.redirectUri
            };

            await exchangeTokenAndComplete(authResult);
          } else if (event.data.error) {
            setLoading(false);
            setError(event.data.error_description || event.data.error || 'Erro de autorização');
          }
        }
      };

      window.addEventListener('message', handleMessage);

      // Monitor window closing
      const checkWindowClosed = setInterval(() => {
        if (authWindow.closed) {
          clearInterval(checkWindowClosed);
          window.removeEventListener('message', handleMessage);

          // Check for fallback data in sessionStorage
          const authCode = sessionStorage.getItem('meta_auth_code');
          const authState = sessionStorage.getItem('meta_auth_state');

          if (authCode && authState) {
            // Clear sessionStorage data
            sessionStorage.removeItem('meta_auth_code');
            sessionStorage.removeItem('meta_auth_state');

            setLoading(false);
            setSuccess('✅ Acesso concedido com sucesso!');

            const authResult = {
              platform: 'meta',
              appId: appId,
              authorizationCode: authCode,
              state: authState,
              permissions: leadAdsPermissions,
              apiVersion: API_VERSION,
              grantedAt: new Date().toISOString(),
              redirectUri: urlRedirect
            };

            exchangeTokenAndComplete(authResult);
          } else if (!success && !error) {
            setLoading(false);
            setError('A janela de autorização foi fechada sem completar o processo.');
          }
        }
      }, 1000);

      // Security timeout (5 minutes)
      setTimeout(() => {
        if (!authWindow.closed) {
          clearInterval(checkWindowClosed);
          window.removeEventListener('message', handleMessage);
          authWindow.close();
          setLoading(false);
          setError('Tempo limite de autorização expirado. Tente novamente.');
        }
      }, 300000);

    } catch (err) {
      setLoading(false);
      setError(err.message || 'Erro ao iniciar o processo de autorização.');
    }
  };

  const exchangeTokenAndComplete = async (authResult) => {
    try {
      const appSecret = process.env.REACT_APP_META_APP_SECRET;
      if (!appSecret) {
        throw new Error('App Secret não configurado');
      }

      const keyData = await metaService.exchangeCodeForKey(
        authResult.authorizationCode
      );
      const normalizedKeyData = normalizeMetaKeyResponse(keyData);

      if (!normalizedKeyData.tokenKey) {
        throw new Error('O endpoint /key não retornou um token de acesso Meta válido. Verifique o payload retornado pelo backend.');
      }

      const completeAuthData = {
        ...authResult,
        tokenKey: normalizedKeyData.tokenKey,
        appName: normalizedKeyData.appName,
        appId: normalizedKeyData.appId,
      };

      setCompleteAuthData(completeAuthData);
      setIsAuthenticated(true);
      setSuccess('✅ Autenticação concluída com sucesso!');
      onAuthSuccess(completeAuthData);
    } catch (err) {
      setLoading(false);
      setError(`Erro ao obter token: ${err.message}`);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCompleteAuthData(null);
    setError(null);
    setSuccess('');
    onAuthSuccess(null);
  };

  if (isAuthenticated && completeAuthData) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Card sx={{ maxWidth: 400, mx: 'auto', mb: 3 }}>
          <CardContent>
            {/* <Avatar sx={{ mx: 'auto', mb: 2, bgcolor: 'primary.main' }}>
              <Facebook />
            </Avatar> */}
            <Typography variant="h6" gutterBottom>
              Autenticação Concluída
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>Aplicativo:</strong> {completeAuthData.appName}
            </Typography>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleLogout}
              size="small"
            >
              Desconectar
            </Button>
          </CardContent>
        </Card>
        <Alert severity="success">
          Autenticação concluída! Você pode prosseguir para a próxima etapa.
        </Alert>
      </Box>
    );
  }

  return (
    <Card sx={{ maxWidth: 500, mx: 'auto' }}>
      <CardContent>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: 1
        }}>
          <Typography variant="h5" gutterBottom>
            Autenticação Meta Business
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Conceda acesso para ler dados dos formulários Lead Ads
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {!appId && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Configuração necessária:</strong>
              <br />
              A variável de ambiente <code>REACT_APP_META_APP_ID</code> não está configurada.
              <br />
              Configure-a no seu arquivo .env para continuar.
            </Typography>
          </Alert>
        )}

        {appId && (
          <Box sx={{ mb: 2, p: 2, backgroundColor: '#f0f7ff', borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              📋 {process.env.REACT_APP_META_APP_NAME}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>App ID:</strong> {appId}
            </Typography>
          </Box>
        )}

        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleMetaLogin}
          disabled={loading || !appId}
          startIcon={loading ? <CircularProgress size={20} /> : <Facebook />}
          sx={{
            backgroundColor: '#1877F2',
            '&:hover': { backgroundColor: '#166FE5' },
            py: 1.5
          }}
        >
          {loading ? 'Aguardando autorização...' : 'Conectar com Meta'}
        </Button>

        <Box sx={{ mt: 2, p: 2, backgroundColor: '#f0f7ff', borderRadius: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            🔑 Permissões que serão solicitadas:
          </Typography>
          <List dense sx={{ pt: 0, pb: 0 }}>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon><CheckCircle fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText
                primary="Recuperação de Leads"
                secondary="Acessar dados de formulários Lead Ads preenchidos"
              />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon><Business fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText
                primary="Listagem de Páginas"
                secondary="Visualizar páginas do Facebook associadas à conta"
              />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon><Analytics fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText
                primary="Leitura de Engajamento"
                secondary="Acessar dados básicos de engajamento da página"
              />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon><Analytics fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText
                primary="Gerenciamento de Anúncios"
                secondary="Permitir que o app acesse a API de Marketing"
              />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon><Analytics fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText
                primary="Gerenciamento de Negócios"
                secondary="Ler e escrever com a API do Business Manager"
              />
            </ListItem>
          </List>

          <Box sx={{ mt: 2, p: 1, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
            <Typography variant="body2" color="success.dark" sx={{ fontWeight: 500 }}>
              ✅ O acesso aos dados será usado para integrar Lead Ads com o CRM GIMOV.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>⚠️ Importante:</strong>
            <br />
            • <strong>Tenha seu Pixel ID disponível para configuração</strong>
            <br />
            • Os formulários Lead Ads devem estar ativos em suas páginas
            <br />
            • Você será redirecionado para o Facebook para autorizar o acesso
            <br />
            • Certifique-se de estar logado na conta correta do Facebook
            <br />
            • O App ID é carregado automaticamente da configuração
            <br />
            • <strong>Apenas dados de leads serão acessados - sem criação/edição de anúncios</strong>
          </Typography>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <Typography variant="caption" color="text.secondary">
            Meta API {API_VERSION} • Autorização segura via OAuth 2.0
          </Typography>
          <Typography variant="h5" component="span">
            Gate51 Sistemas de Informação - Meta Provider Technology
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MetaAuth;